// src/components/Campaigns/CampaignsTable.jsx
import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Box,
  Typography,
  CircularProgress,
  TablePagination,
  Tooltip,
  LinearProgress,
  // IconButton, // Removed as we are no longer using IconButton directly for these actions
  useTheme, // For responsive design
} from "@mui/material";
// import { FaFilePdf } from "react-icons/fa"; // Removed as icon is no longer needed in button
// import { Visibility as VisibilityIcon } from "@mui/icons-material"; // Removed as icon is no longer needed in button
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import logo from '../../../../../assets/logo.png'; // Adjust path as needed
import { toast } from "react-toastify";
import baseURL from "../../config";
import COLORS from "../../../../../constants/colors";
import { formatCurrency, getStatusChip, formatDate } from "./campaignsUtils"; // Import utility functions
import CampaignDetailsDialog from "./CampaignDetailsDialog"; // Import the new dialog

const CampaignsTable = ({ campaigns, loading, onCampaignUpdated, handleDownloadLog }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const theme = useTheme(); // For responsive styling

  // Keep selectedCampaign in sync when campaigns data refreshes (e.g. after image update)
  useEffect(() => {
    if (selectedCampaign) {
      const updated = campaigns.find(c => c.id === selectedCampaign.id);
      if (updated) setSelectedCampaign(updated);
    }
  }, [campaigns]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDetails = (campaign) => {
    setSelectedCampaign(campaign);
    setDialogOpen(true);
  };

  const handleCloseDetails = () => {
    setDialogOpen(false);
    setSelectedCampaign(null);
  };

  const handleDialogCampaignUpdated = () => {
    if (onCampaignUpdated) {
      onCampaignUpdated(); // Trigger refresh in parent (CampaignManager)
    }
    // No need to close dialog here, CampaignDetailsDialog already closes it.
  };

  return (
    <Paper
      sx={{
        backgroundColor: "#fff",
        borderRadius: "16px",
        boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
        border: "1px solid rgba(0,0,0,0.05)",
        p: 3,
        mb: 2,
      }}
    >
      <TableContainer>
        <Table size="small">
          <TableHead sx={{ backgroundColor: "#f8f9fa" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", whiteSpace: 'nowrap' }}>Name</TableCell>
              <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", whiteSpace: 'nowrap' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", whiteSpace: 'nowrap' }}>Goal</TableCell>
              <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", whiteSpace: 'nowrap' }}>Raised</TableCell>
              <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", whiteSpace: 'nowrap' }}>Progress</TableCell>
              <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", whiteSpace: 'nowrap' }}>Donors</TableCell>
              <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", whiteSpace: 'nowrap' }}>Created</TableCell>
              <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", whiteSpace: 'nowrap' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={30} color="primary" />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Loading campaigns...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : campaigns.length > 0 ? (
              campaigns.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((campaign) => {
                const percentage = (campaign.raised / campaign.goal) * 100;
                return (
                  <TableRow key={campaign.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell sx={{ py: 1.5, fontSize: "0.8rem" }}>{campaign.name}</TableCell>
                    <TableCell sx={{ py: 1.5, fontSize: "0.8rem" }}>{getStatusChip(campaign.status)}</TableCell>
                    <TableCell sx={{ py: 1.5, fontSize: "0.8rem" }}>{formatCurrency(campaign.goal)}</TableCell>
                    <TableCell sx={{ py: 1.5, fontSize: "0.8rem" }}>{formatCurrency(campaign.raised)}</TableCell>
                    <TableCell sx={{ py: 1.5, fontSize: "0.8rem" }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LinearProgress
                          variant="determinate"
                          value={percentage > 100 ? 100 : percentage}
                          sx={{ height: 6, borderRadius: 3, flexGrow: 1, mr: 1, backgroundColor: "#e0e0e0" }}
                          color={percentage >= 100 ? "success" : "primary"}
                        />
                        <Typography variant="caption" sx={{ minWidth: 40, fontSize: "0.75rem" }}>
                          {percentage.toFixed(0)}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 1.5, fontSize: "0.8rem" }}>{campaign.donations || 0}</TableCell>
                    <TableCell sx={{ py: 1.5, fontSize: "0.8rem" }}>{formatDate(campaign.created_at)}</TableCell>
                    <TableCell sx={{ py: 1.5, fontSize: "0.8rem", whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'nowrap', justifyContent: 'flex-start' }}> {/* Changed flexWrap to 'nowrap' */}
                        <Tooltip title="View Details / Edit">
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleOpenDetails(campaign)}
                            // Removed startIcon={<VisibilityIcon />}
                            sx={{
                              textTransform: 'none',
                              fontSize: '0.75rem',
                              padding: '4px 8px',
                              borderColor: COLORS.primary,
                              color: COLORS.primary,
                              '&:hover': {
                                backgroundColor: COLORS.primary,
                                color: '#fff',
                                borderColor: COLORS.primary,
                              },
                            }}
                          >
                            Details
                          </Button>
                        </Tooltip>
                        {campaign.status === 'closed' && ( // Only show PDF for closed campaigns
                          <Tooltip title="View Donors / Generate PDF">
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleDownloadLog(campaign.id, campaign.name)}
                              // Removed startIcon={<FaFilePdf size={14} />}
                              sx={{
                                textTransform: 'none',
                                fontSize: '0.75rem',
                                padding: '4px 8px',
                                borderColor: COLORS.secondary || '#f44336',
                                color: COLORS.secondary || '#f44336',
                                '&:hover': {
                                  backgroundColor: COLORS.secondary || '#f44336',
                                  color: '#fff',
                                  borderColor: COLORS.secondary || '#f44336',
                                },
                              }}
                            >
                              PDF Log
                            </Button>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 2, fontSize: "0.85rem", color: "#777" }}>
                  No campaigns found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {campaigns.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={campaigns.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ fontSize: "0.7rem", '& .MuiTablePagination-toolbar': { minHeight: 'auto' } }}
        />
      )}

      <CampaignDetailsDialog
        open={dialogOpen}
        onClose={handleCloseDetails}
        campaign={selectedCampaign}
        onCampaignUpdated={handleDialogCampaignUpdated}
      />
    </Paper>
  );
};

export default CampaignsTable;